import FirebaseLogin from "@/lib/auth/AuthApi.service";
import { useFirebaseAuthState } from "@/lib/hooks/useFirebaseAuthState.hook";
import type { User as FirebaseUser } from "firebase/auth";
import { jwtDecode } from "jwt-decode";
import {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState,
	useCallback,
} from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import OrganizationService from "../organization.service";
import type { OrgaMembership, SystemRole } from "../auth/auth.types";

type JWTPayload = {
	uid: string;
	email: string;
	displayName: string;
	systemRoles: SystemRole[];
	iat: number;
	exp: number;
	emailVerified: boolean;
};

interface AuthContextType {
	isAuthenticated: boolean;
	isLoading: boolean;
	user: JWTPayload | null;
	organizations: OrgaMembership[];
	currentOrganization: OrgaMembership;
	setCurrentOrganization: (organizationId: string) => void;
	login: (email: string, password: string) => Promise<void>;
	logout: () => Promise<void>;
	setAuthToken: (token: string) => Promise<void>;
	refreshUserAndToken: () => Promise<boolean>;
	refreshOrganizations: () => Promise<boolean>;
	isRefreshing: boolean;
	firebaseUser: FirebaseUser | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Add this constant at the top of the file, after imports
const STORAGE_KEY_ORGANIZATIONS = "taxpeer_organizations";
const STORAGE_KEY_SELECTED_ORGANIZATION = "taxpeer_current_organization_id";
const STORAGE_KEY_AUTH_TAB_LOCK = "taxpeer_auth_tab_lock";
const TAB_LOCK_TIMEOUT = 5000; // 5 seconds

/**
 * Authentication Context for the Taxpeer application
 *
 * React Context is a way to share state and functionality across components without
 * having to pass props manually through each level (prop drilling). It creates a
 * global state that can be accessed by any component within its provider.
 *
 * This AuthContext specifically manages:
 * - Authentication state (isAuthenticated)
 * - Loading state during authentication checks
 * - User information from the JWT token
 * - Login functionality with Firebase
 * - Logout functionality
 * - JWT token management in cookies
 * - Firebase auth state change monitoring
 * - User data refresh functionality
 * - Current organization functionality:
 *   - This is the organization that the user is currently in
 * 	- It's the first organization that is not the default organization
 * 	- If there is no NOT default organization, it's the default organization
 *
 * Usage:
 * 1. Wrap your app with AuthProvider:
 *    ```tsx
 *    <AuthProvider>
 *      <App />
 *    </AuthProvider>
 *    ```
 *
 * 2. Use the auth context in any component:
 *    ```tsx
 *    const { isAuthenticated, user, login, logout, refreshUserAndToken } = useAuth();
 *    ```
 *
 * Authentication Flow:
 * 1. User attempts login with email/password
 * 2. Firebase authenticates credentials
 * 3. Firebase returns a token
 * 4. Token is stored in cookies with secure settings
 * 5. Token is used for subsequent API requests
 *
 * Security Features:
 * - Tokens are stored in HTTP-only cookies (with secure flag in production)
 * - Strict same-site policy
 * - 7-day token expiration
 * - Auto-logout on Firebase auth state changes (e.g., email verification)
 *
 * @see FirebaseLogin for the underlying Firebase authentication implementation
 * @see RequireAuth component for protecting routes
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [isLoading, setIsLoading] = useState(true);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [isSettingToken, setIsSettingToken] = useState(false);
	const [cookies, setCookie, removeCookie] = useCookies(["jwt_token"]);
	const [user, setUser] = useState<JWTPayload | null>(null);
	const [organizations, setOrganizations] = useState<OrgaMembership[]>(() => {
		const stored = localStorage.getItem(STORAGE_KEY_ORGANIZATIONS);
		return stored ? JSON.parse(stored) : [];
	});
	const [selectedOrganizationId, setSelectedOrganizationId] = useState<
		string | null
	>(() => null);
	const navigate = useNavigate();
	const firebaseLogin = FirebaseLogin.getInstance();

	// Use our custom Firebase auth state hook
	const { firebaseUser, loading: firebaseLoading } = useFirebaseAuthState();

	// Generate unique tab ID for debugging multi-tab issues
	const [tabId] = useState(
		() => `tab-${Math.random().toString(36).substr(2, 9)}`,
	);

	// Multi-tab auth lock helpers
	const acquireAuthLock = useCallback((): boolean => {
		const now = Date.now();
		const existingLock = localStorage.getItem(STORAGE_KEY_AUTH_TAB_LOCK);

		if (existingLock) {
			const { tabId: lockTabId, timestamp } = JSON.parse(existingLock);

			// If lock is held by this tab or expired, we can proceed
			if (lockTabId === tabId || now - timestamp > TAB_LOCK_TIMEOUT) {
				localStorage.setItem(
					STORAGE_KEY_AUTH_TAB_LOCK,
					JSON.stringify({ tabId, timestamp: now }),
				);
				return true;
			}

			return false;
		}

		// No existing lock, acquire it
		localStorage.setItem(
			STORAGE_KEY_AUTH_TAB_LOCK,
			JSON.stringify({ tabId, timestamp: now }),
		);
		return true;
	}, [tabId]);

	const releaseAuthLock = useCallback((): void => {
		const existingLock = localStorage.getItem(STORAGE_KEY_AUTH_TAB_LOCK);
		if (existingLock) {
			const { tabId: lockTabId } = JSON.parse(existingLock);
			if (lockTabId === tabId) {
				localStorage.removeItem(STORAGE_KEY_AUTH_TAB_LOCK);
			}
		}
	}, [tabId]);

	// derive the current organization from the list stored in localStorage/state
	// Preference: first organization with isDefault === false; otherwise, the default one (isDefault === true); otherwise, the first entry
	type StoredOrgaMembership = OrgaMembership & { isDefault?: boolean };
	const currentOrganizationMaybe: OrgaMembership | null = useMemo(() => {
		if (!organizations || organizations.length === 0) return null;
		const orgs = organizations as StoredOrgaMembership[];
		if (selectedOrganizationId) {
			const selected = orgs.find(
				(o) => o.organizationId === selectedOrganizationId,
			);
			if (selected) return selected;
		}
		const nonDefault = orgs.find((o) => o.isDefault === false);
		if (nonDefault) return nonDefault;
		const def = orgs.find((o) => o.isDefault === true);
		return def ?? orgs[0];
	}, [organizations, selectedOrganizationId]);

	/**
	 * Fetches the user's organization memberships from the API
	 * This is separate from the JWT token to ensure we have the most up-to-date data
	 */
	const refreshOrganizations = useCallback(async (): Promise<boolean> => {
		try {
			const organizationService = OrganizationService.getInstance();
			const orgs = await organizationService.getUserOrganizations();
			setOrganizations(orgs);
			localStorage.setItem(STORAGE_KEY_ORGANIZATIONS, JSON.stringify(orgs));
			return true;
		} catch (error) {
			console.error("Failed to fetch organizations:", error);
			return false;
		}
	}, []);

	/**
	 * This will fetch additional user data from the backend
	 * - like organizations
	 */
	const doUserUpdates = useCallback(async () => {
		await refreshOrganizations();
	}, [refreshOrganizations]);

	/**
	 * This function sets the auth token in the cookies
	 *
	 * - refreshes the organizations
	 * @param token
	 */
	const setAuthToken = useCallback(
		async (token: string) => {
			// Try to acquire lock for multi-tab coordination
			if (!acquireAuthLock()) {
				return;
			}

			setIsSettingToken(true);

			try {
				setCookie("jwt_token", token, {
					path: "/",
					maxAge: 7 * 24 * 60 * 60, // 7 days
					secure: process.env.NODE_ENV === "production",
					sameSite: "strict",
				});

				// Allow a short delay for cookie propagation
				await new Promise((resolve) => setTimeout(resolve, 50));

				// Verify cookie was set
				const cookieSetSuccessfully = document.cookie.includes("jwt_token=");
				if (!cookieSetSuccessfully) {
					throw new Error("Failed to set authentication cookie");
				}

				// fetch additional user data
				try {
					await doUserUpdates();
				} catch (error) {
					console.error("Failed to fetch user data after login:", error);
				}
			} finally {
				setIsSettingToken(false);
				releaseAuthLock();
			}
		},
		[setCookie, acquireAuthLock, releaseAuthLock, doUserUpdates],
	);

	// Allows platform managers to choose the current organization explicitly via UI
	const setCurrentOrganization = useCallback(
		(organizationId: string) => {
			setSelectedOrganizationId(organizationId);
			const uid = user?.uid;
			if (uid) {
				const key = `${STORAGE_KEY_SELECTED_ORGANIZATION}:${uid}`;
				localStorage.setItem(key, organizationId);
			} else {
				throw new Error(
					"User is not authenticated but tries to set the current organization",
				);
			}
		},
		[user],
	);

	/**
	 * Refreshes the user's JWT token by getting a new Firebase token
	 * and exchanging it for a new JWT token from the backend
	 *
	 * This is useful when user data has changed (like display name or email verification status)
	 * and you need to update the JWT token without requiring a full logout and login.
	 *
	 * @returns A promise that resolves to true if the refresh was successful, false otherwise
	 */
	const refreshUserAndToken = useCallback(async (): Promise<boolean> => {
		try {
			setIsRefreshing(true);

			// Get the current Firebase user
			const currentUser = firebaseLogin.auth.currentUser;

			if (!currentUser) {
				toast.error("Keine Benutzeranmeldung gefunden");
				return false;
			}

			// Get a fresh Firebase token
			const firebaseToken = await currentUser.getIdToken(true);

			// Exchange it for a new JWT token from our backend
			const response =
				await firebaseLogin.upgradeFirebaseIdToJWT(firebaseToken);

			// Update the token in the auth context
			await setAuthToken(response.token);
			console.log("Refreshed user token");
			return true;
		} catch (error) {
			console.error("Failed to refresh user token:", error);
			toast.error("Fehler beim Aktualisieren der Benutzerdaten");
			return false;
		} finally {
			setIsRefreshing(false);
		}
	}, [firebaseLogin, setAuthToken]);

	/**
	 * Logs in the user with the given email and password
	 * @param email
	 * @param password
	 */
	const login = async (email: string, password: string) => {
		const response = await firebaseLogin.login({ email, password });
		await setAuthToken(response.token);
		navigate("/dashboard");
	};

	// logs the user out and removes the cookie
	const logout = useCallback(async () => {
		// Try to acquire lock for multi-tab coordination
		if (!acquireAuthLock()) {
			return;
		}

		try {
			await firebaseLogin.logout();

			// we need to remove the cookie with the same path, otherwise it will not be removed properly or may not be removed at all
			removeCookie("jwt_token", {
				path: "/",
				secure: process.env.NODE_ENV === "production",
				sameSite: "strict",
			});

			// Clear the user state and localStorage
			localStorage.removeItem(STORAGE_KEY_ORGANIZATIONS);
			// we do not delete the STORAGE_KEY_SELECTED_ORGANIZATION, we want that if the user logs in again, he is redirected to the same organization as they prefers.
			setUser(null);
			setOrganizations([]);
			setSelectedOrganizationId(null);
			navigate("/auth/login");
		} finally {
			releaseAuthLock();
		}
	}, [firebaseLogin, removeCookie, navigate, acquireAuthLock, releaseAuthLock]);

	// This actually checks if the user is authenticated and decodes the JWT token to get user information
	useEffect(() => {
		if (cookies.jwt_token) {
			try {
				// Decode the JWT token to get user information
				const decodedToken = jwtDecode<JWTPayload>(cookies.jwt_token);
				setUser(decodedToken);
			} catch (error) {
				console.error("Failed to decode JWT token:", error);
				setUser(null);
			}
		} else {
			setUser(null);
		}
		setIsLoading(false);
	}, [cookies.jwt_token]);

	// Handle Firebase auth state changes
	useEffect(() => {
		// Skip if still loading Firebase auth state
		if (firebaseLoading) {
			return;
		}

		// If we have a JWT token but no Firebase user, log out
		// BUT add protection against immediate logouts during token setting
		if (!firebaseUser && cookies.jwt_token && !isSettingToken) {
			console.log("Firebase user is null but JWT token exists, logging out");
			logout();
			return;
		}

		// If we have both Firebase user and JWT token, check if they're in sync
		if (firebaseUser && cookies.jwt_token) {
			try {
				const decodedToken = jwtDecode<JWTPayload>(cookies.jwt_token);

				// Check if email verification status changed
				if (decodedToken.emailVerified !== firebaseUser.emailVerified) {
					console.log(
						"Email verification status changed, logging out to refresh token",
					);
					toast.success(
						"Deine E-Mail-Adresse wurde verifiziert. Bitte melde dich erneut an.",
					);
					logout();
					return;
				}

				if (!firebaseUser.email) {
					console.log(
						"Firebase user has no email, we are likely in impersonation mode",
					);
					return;
				}

				if (decodedToken.email !== firebaseUser.email) {
					console.log("Email address changed, logging out to refresh token");
					toast.success(
						"Deine E-Mail-Adresse wurde geändert. Bitte melde dich erneut an.",
					);
					logout();
					return;
				}
			} catch (error) {
				console.error("Error comparing JWT with Firebase user:", error);
			}
		}
	}, [
		firebaseUser,
		firebaseLoading,
		cookies.jwt_token,
		logout,
		isSettingToken,
	]);

	// Load user-specific selected organization (and migrate legacy key) when user becomes available
	useEffect(() => {
		const uid = user?.uid;
		if (!uid) return;

		const userKey = `${STORAGE_KEY_SELECTED_ORGANIZATION}:${uid}`;
		const userSpecific = localStorage.getItem(userKey);
		if (userSpecific) {
			setSelectedOrganizationId(userSpecific);
			return;
		}
	}, [user]);

	// Add event listener for token expiration
	useEffect(() => {
		const handleTokenExpired = () => {
			toast.error("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");
			logout();
		};

		window.addEventListener("auth:tokenExpired", handleTokenExpired);
		window.addEventListener("auth:refreshToken", refreshUserAndToken);

		return () => {
			window.removeEventListener("auth:tokenExpired", handleTokenExpired);
			window.removeEventListener("auth:refreshToken", refreshUserAndToken);
		};
	}, [logout, refreshUserAndToken]); // Added logout and refreshUserAndToken to the dependency array

	// Ensure organizations are loaded when authenticated
	const isAuthenticated = !!cookies.jwt_token;
	useEffect(() => {
		if (isAuthenticated && organizations.length === 0) {
			refreshOrganizations();
		}
	}, [isAuthenticated, organizations.length, refreshOrganizations]);

	// If authenticated but organization not yet resolved, delay rendering
	if (isAuthenticated && !currentOrganizationMaybe) {
		return null;
	}

	// Fallback when unauthenticated to satisfy non-null type
	// this is not used anywhere, but it is needed
	// satisfy the non-null type otherwise we need to null check all the time when we access the currentOrganization hook
	const UNAUTHENTICATED_ORG: OrgaMembership = {
		organizationId: "",
		orgaRoles: [],
	};

	const value = {
		isAuthenticated,
		isLoading:
			isLoading ||
			firebaseLoading ||
			isSettingToken ||
			(isAuthenticated && !currentOrganizationMaybe),
		isRefreshing,
		user,
		organizations,
		currentOrganization: currentOrganizationMaybe ?? UNAUTHENTICATED_ORG,
		setCurrentOrganization,
		login,
		logout,
		setAuthToken,
		refreshUserAndToken,
		refreshOrganizations,
		firebaseUser,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};
